import { useGetProviderConfig, useUpsertProviderConfig } from "@/hooks/useProviderConfig"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { useApp } from "../../../AppProvider"
import { Spinner } from "../../common"
import { ErrorMessage } from "../../common/Feedback/ErrorMessage"

interface ZoetisConfigFormProps {
  orgId: string
}

interface ZoetisConfig {
  clientId: string
  password: string
}

export const ZoetisConfigForm = ({ orgId }: ZoetisConfigFormProps) => {
  const { token, addToast } = useApp()

  const { data: initialConfig, isLoading, isError, error } = useGetProviderConfig(orgId, "ZOETIS", token)

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isDirty },
    reset,
  } = useForm<ZoetisConfig>({
    defaultValues: {
      clientId: initialConfig?.config?.clientId || "",
      password: initialConfig?.config?.password || "",
    },
  })

  useEffect(() => {
    if (initialConfig?.config) {
      reset({
        clientId: initialConfig.config.clientId || "",
        password: initialConfig.config.password || "",
      })
    }
  }, [initialConfig, reset])

  const mutation = useUpsertProviderConfig(orgId, "ZOETIS", token)

  const onSubmit = handleSubmit(async (data) => {
    const res = await mutation.mutateAsync({
      provider: "ZOETIS",
      config: data,
    })
    if (res.data) {
      addToast("Success", "Zoetis config saved", "success")
    } else {
      addToast("Error", "Failed to save config", "error")
    }
  })

  if (isLoading) return <Spinner />

  if (isError && !error.message.includes("not found")) return <ErrorMessage message={error?.message} />

  return (
    <div>
      <h3 className="text-md font-semibold text-gray-800">Zoetis</h3>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label">Client ID</label>
          <input className="input" {...register("clientId", { required: "ClientId is required" })} />
          {errors.clientId && <div className="error-message">{errors.clientId.message}</div>}
        </div>
        <div>
          <label className="label">Password</label>
          <input
            className="input"
            type="password"
            {...register("password", { required: "Password is required" })}
          />
          {errors.password && <div className="error-message">{errors.password.message}</div>}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="btn-secondary"
            disabled={!isDirty}
            onClick={() =>
              reset({
                clientId: initialConfig?.config?.clientId || "",
                password: initialConfig?.config?.password || "",
              })
            }
          >
            Cancel
          </button>

          <button className="btn" type="submit" disabled={!isDirty || isSubmitting || mutation.isPending}>
            {mutation.isPending ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  )
}
